import { ApiScoreCardShareResponse, ApiScoreCardv2 } from '@/types/api';
import { cachedGet, DEFAULT_TTL_MS } from './cache';
import { mockApiScoreCards, mockApiScoreCardsV2, mockApiSessions } from '@/mocks';
import { quickLog } from '@/utils';
import { USE_MOCK_DATA } from '@/constants';
import authenticatedAxios from '@/lib/axios';

/**
 * Get scorecard for a session
 */
export const getScoreCardBySession = async (sessionId: number): Promise<ApiScoreCardv2> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock scorecard for session ID:', sessionId);
    const scoreCard = mockApiScoreCards.find((sc) => sc.session_id === sessionId);
    if (!scoreCard) throw new Error(`ScoreCard for session ${sessionId} not found`);
    return mockApiScoreCardsV2[0];
  }

  const endpoint = `app/scorecards/session/${sessionId}/scorecard`;
  const response = await authenticatedAxios.get<ApiScoreCardv2>(endpoint);
  return response.data;
};

/**
 * Get all scorecards for a user
 */
export const getScoreCardsByUser = async (userId: string): Promise<ApiScoreCardv2[]> => {
  if (USE_MOCK_DATA) {
    // Filter by user through sessions
    quickLog.api('[MOCK DATA] Returning mock scorecards for user ID:', userId);
    const userSessions = mockApiSessions.filter((s) => s.owner_user_id === userId);
    const userSessionIds = userSessions.map((s) => s.id);
    return mockApiScoreCardsV2;
  }

  const endpoint = `app/scorecards/user/${userId}/scorecard`;
  return cachedGet(
    `scorecards.user:${userId}`,
    async () => {
      const response = await authenticatedAxios.get<ApiScoreCardv2[]>(endpoint);
      return response.data;
    },
    DEFAULT_TTL_MS,
  );
};

/**
 * Get all v2 scorecards for a user
 */
export const getScoreCardsByUserV2 = async (
  userId: string,
  forceMock?: boolean,
): Promise<ApiScoreCardv2[]> => {
  if (forceMock || USE_MOCK_DATA) {
    // Filter by user through sessions
    quickLog.api('[MOCK DATA] Returning mock v2 scorecards for user ID:', userId);
    return mockApiScoreCardsV2;
  }

  const endpoint = `app/scorecards/user/${userId}/scorecard`;
  return cachedGet(
    `scorecardsV2.user:${userId}`,
    async () => {
      const response = await authenticatedAxios.get<ApiScoreCardv2[]>(endpoint);
      return response.data;
    },
    DEFAULT_TTL_MS,
  );
};

/**
 * Share Scorecard
 *
 * Share a scorecard with another user.
 *
 *  - By default shares with the user's manager.
 *  - If `shareWithUserId` is provided, shares with that user instead.
 */
export const shareScorecardWithManager = async ({
  userId,
  scoreCardId,
  sessionId,
  shareWithUserId,
}: {
  /** The ID of the user sharing the scorecard */
  userId: string;
  /** The ID of the scorecard to be shared */
  scoreCardId: number;
  /** The ID of the session associated with the scorecard */
  sessionId: number;
  /** Optional: The ID of the user to share with (defaults to user's manager if not provided) */
  shareWithUserId?: number;
}) => {
  if (USE_MOCK_DATA) {
    return [];
  }

  const endpoint = '/app/scorecards/share';
  const data = {
    user_id: userId,
    score_card_id: scoreCardId,
    session_id: sessionId,
    share_with_user_id: shareWithUserId,
  };

  const response = await authenticatedAxios.post<ApiScoreCardShareResponse[]>(endpoint, data);
  return response.data;
};
